import { PaginationState } from '@tanstack/react-table';
import { useId } from 'react';
import { create, StateCreator, StoreApi } from 'zustand';
import { persist } from 'zustand/middleware';

interface PaginationStore {
  pagination: PaginationState;
  setPagination: (
    pagination: PaginationState | ((prev: PaginationState) => PaginationState),
  ) => void;
  resetPagination: () => void;
}

const paginators: Record<string, StoreApi<PaginationStore>> = {};

const getStateCreator =
  (pageSize = 20): StateCreator<PaginationStore> =>
  (set) => ({
    pagination: { pageIndex: 0, pageSize },
    setPagination: (updater) => {
      set((state) => ({
        pagination:
          typeof updater === 'function' ? updater(state.pagination) : updater,
      }));
    },
    resetPagination() {
      set({ pagination: { pageIndex: 0, pageSize } });
    },
  });

type CreateStoreOptions = { shouldPersist?: boolean; pageSize?: number };

const createPaginationStore = (id: string, options?: CreateStoreOptions) =>
  options?.shouldPersist
    ? create<PaginationStore>()(
        persist(getStateCreator(options?.pageSize), {
          name: `${id}-pagination`,
          merge: (persistedState, currentState) => {
            if ((persistedState as PaginationStore).pagination.pageSize <= 20)
              return currentState;
            return {
              ...currentState,
              pagination: { ...currentState.pagination, pageSize: 20 },
            };
          },
        }),
      )
    : create<PaginationStore>(getStateCreator(options?.pageSize));

export const usePaginationStore = (
  id: string,
  options: Pick<CreateStoreOptions, 'pageSize'> = {},
) => {
  if (!paginators[id]) {
    paginators[id] = createPaginationStore(id, {
      shouldPersist: true,
      ...options,
    });
  }
  return paginators[id];
};
